import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, Copy, Check, QrCode, Loader } from 'lucide-react'
import GlassCard from '@/components/ui/GlassCard'
import api from '@/services/api'

interface AgentQRModalProps {
  isOpen: boolean
  onClose: () => void
}

const AgentQRModal: React.FC<AgentQRModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [qrData, setQrData] = useState<{
    qrBase64: string
    landingUrl: string
    agentName: string
    subdomain: string
  } | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchQR()
    }
  }, [isOpen])

  const fetchQR = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/agents/my-qr')
      setQrData(response.data)
    } catch (err) {
      console.error('Error fetching QR:', err)
      setError('Error al generar el código QR')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!qrData) return
    
    const link = document.createElement('a')
    link.href = qrData.qrBase64
    link.download = `qr-${qrData.subdomain}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCopyUrl = async () => {
    if (!qrData) return
    
    try {
      await navigator.clipboard.writeText(qrData.landingUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Error copying URL:', error)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <GlassCard className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Tu Código QR
                </h2>
                <p className="text-white/70 text-sm">
                  Comparte tu landing page profesional
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="h-12 w-12 text-blue-400 animate-spin mb-4" />
                <p className="text-white/70">Generando código QR...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <QrCode className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <p className="text-red-400">{error}</p>
                <button
                  onClick={fetchQR}
                  className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
                >
                  Reintentar
                </button>
              </div>
            ) : qrData && (
              <>
                {/* QR Preview */}
                <div className="bg-white rounded-2xl p-6 mb-6 flex items-center justify-center">
                  <img
                    src={qrData.qrBase64}
                    alt="QR Code"
                    className="w-64 h-64 object-contain"
                  />
                </div>

                {/* Info Text */}
                <div className="text-center mb-6">
                  <p className="text-white/90 text-sm mb-3">
                    📱 Escanea este código para visitar tu landing page
                  </p>
                  <div className="bg-white/10 rounded-lg px-4 py-3 mb-4">
                    <p className="text-white/60 text-xs mb-1">Tu sitio web:</p>
                    <p className="text-white text-sm font-mono">
                      {qrData.landingUrl}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleDownload}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 transition-all transform hover:scale-105"
                  >
                    <Download className="h-5 w-5" />
                    Descargar QR HD (1000x1000px)
                  </button>

                  <button
                    onClick={handleCopyUrl}
                    className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="h-5 w-5 text-green-400" />
                        ¡Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="h-5 w-5" />
                        Copiar URL
                      </>
                    )}
                  </button>
                </div>

                {/* Note */}
                <p className="text-white/50 text-xs text-center mt-4">
                  Ideal para tarjetas de presentación, volantes y redes sociales.
                  Alta resolución (1000x1000px) para impresión profesional.
                </p>
              </>
            )}
          </GlassCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default AgentQRModal
